import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '../../config/axiosConfig';
import type { Granularity, Period } from './periods';
import { resolveBucketColumns } from './bucketColumns';
import type { UsageRow } from './bucketColumns';

/**
 * App usage: unique users vs visits, per app, broken down by hospital and then
 * by department.
 *
 * The app table is the entry point and every app row expands in place to show
 * its own hospitals, so the hospital split of each app is visible without
 * leaving the list. A hospital sub-row drills one level further, to the
 * departments of that app in that hospital.
 *
 * Every failure path ends in an empty table plus a neutral Hebrew note — no
 * stack traces, no error codes, no half-rendered numbers. A broken stats query
 * must look like "no data yet", never like a malfunction.
 */

type Scope = 'app' | 'hospital' | 'department';
type Metric = 'users' | 'visits';

interface UsageResponse {
  rows: UsageRow[];
  /** Every bucket in the range, including the ones nobody used the app in. */
  bucketStarts?: string[];
}

interface Selection {
  key: string;
  label: string;
}

/** One app's hospital split, fetched lazily when its row is expanded. */
interface Breakdown {
  loading: boolean;
  rows: UsageRow[];
  bucketStarts: string[];
  unavailable: boolean;
}

const formatBucketLabel = (iso: string, granularity: Granularity): string => {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    if (granularity === 'month') {
      return date.toLocaleDateString('he-IL', { month: 'short', year: '2-digit' });
    }
    return date.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};

/** Enter/Space on a row behaves like a click, since `tr` has no native role. */
const activationProps = (handler?: () => void) => {
  if (!handler) return {};
  return {
    onClick: handler,
    tabIndex: 0,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
      }
    },
  };
};

const AppUsageSection: React.FC<{ period: Period }> = ({ period }) => {
  const [metric, setMetric] = useState<Metric>('users');
  const [selectedApp, setSelectedApp] = useState<Selection | null>(null);
  const [selectedHospital, setSelectedHospital] = useState<Selection | null>(null);

  const [rows, setRows] = useState<UsageRow[]>([]);
  const [rangeBuckets, setRangeBuckets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  // Which app rows are open, and the hospital split behind each. The cache is
  // keyed by period as well as app, so switching the range refetches rather
  // than showing last range's numbers under this range's columns.
  const [expandedApps, setExpandedApps] = useState<string[]>([]);
  const [breakdowns, setBreakdowns] = useState<Record<string, Breakdown>>({});

  const scope: Scope = selectedHospital ? 'department' : selectedApp ? 'hospital' : 'app';

  // Guards against a slow response for an old selection landing after a fast
  // one for the new selection and overwriting the table with stale numbers.
  const requestSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setLoading(true);
    setUnavailable(false);
    try {
      const params = new URLSearchParams({
        scope,
        granularity: period.granularity,
        from: period.from().toISOString(),
      });
      if (selectedApp) params.set('appName', selectedApp.key);
      if (selectedHospital) params.set('hospital', selectedHospital.key);

      const res = await API.get<UsageResponse>(`/api/analytics/app-usage?${params.toString()}`, {
        handlingErrorLocally: true,
      });
      if (requestSeqRef.current !== seq) return;
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setRangeBuckets(Array.isArray(res.data?.bucketStarts) ? res.data.bucketStarts : []);
    } catch {
      if (requestSeqRef.current !== seq) return;
      setRows([]);
      setRangeBuckets([]);
      setUnavailable(true);
    } finally {
      if (requestSeqRef.current === seq) setLoading(false);
    }
  }, [period, scope, selectedApp, selectedHospital]);

  useEffect(() => {
    void load();
  }, [load]);

  const breakdownKey = useCallback((appKey: string) => `${period.id}|${appKey}`, [period]);

  /**
   * One app's hospitals. This is the same `scope=hospital&appName=…` query the
   * hospital view issues, so the expanded numbers and the drilled-in ones come
   * from one aggregation path and cannot disagree.
   */
  const loadBreakdown = useCallback(
    async (appKey: string) => {
      const cacheKey = breakdownKey(appKey);
      setBreakdowns((prev) => ({
        ...prev,
        [cacheKey]: { loading: true, rows: [], bucketStarts: [], unavailable: false },
      }));

      try {
        const params = new URLSearchParams({
          scope: 'hospital',
          granularity: period.granularity,
          from: period.from().toISOString(),
          appName: appKey,
        });
        const res = await API.get<UsageResponse>(`/api/analytics/app-usage?${params.toString()}`, {
          handlingErrorLocally: true,
        });
        setBreakdowns((prev) => ({
          ...prev,
          [cacheKey]: {
            loading: false,
            rows: Array.isArray(res.data?.rows) ? res.data.rows : [],
            bucketStarts: Array.isArray(res.data?.bucketStarts) ? res.data.bucketStarts : [],
            unavailable: false,
          },
        }));
      } catch {
        setBreakdowns((prev) => ({
          ...prev,
          [cacheKey]: { loading: false, rows: [], bucketStarts: [], unavailable: true },
        }));
      }
    },
    [breakdownKey, period]
  );

  // Fetches anything open but not yet cached — which covers both "just
  // expanded" and "still expanded after the range changed". `loadBreakdown`
  // writes its loading placeholder synchronously, so a key is never requested
  // twice.
  useEffect(() => {
    if (scope !== 'app') return;
    for (const appKey of expandedApps) {
      if (!breakdowns[breakdownKey(appKey)]) void loadBreakdown(appKey);
    }
  }, [scope, expandedApps, breakdowns, breakdownKey, loadBreakdown]);

  const bucketColumns = useMemo(() => resolveBucketColumns(rangeBuckets, rows), [rangeBuckets, rows]);

  const valueFor = (row: UsageRow, bucketStart: string): number => {
    const bucket = (row.buckets || []).find((b) => b.bucketStart === bucketStart);
    if (!bucket) return 0;
    return metric === 'users' ? bucket.users : bucket.visits;
  };

  const toggleApp = (appKey: string) => {
    setExpandedApps((prev) =>
      prev.includes(appKey) ? prev.filter((k) => k !== appKey) : [...prev, appKey]
    );
  };

  const drillIntoHospital = (app: Selection, hospital: UsageRow) => {
    setSelectedApp(app);
    setSelectedHospital({ key: hospital.key, label: hospital.label });
  };

  const isAppScope = scope === 'app';
  const scopeHeader = scope === 'app' ? 'אפליקציה' : scope === 'hospital' ? 'בית חולים' : 'מחלקה';
  // Label column + every bucket + the total column.
  const fullWidth = bucketColumns.length + 2;

  return (
    <section className="stats-usage">
      <h2 className="stats-section-title">שימוש באפליקציות</h2>

      <div className="stats-usage-toggle" role="group" aria-label="מדד">
        <button
          type="button"
          className={metric === 'users' ? 'is-active' : ''}
          onClick={() => setMetric('users')}
        >
          משתמשים ייחודיים
        </button>
        <button
          type="button"
          className={metric === 'visits' ? 'is-active' : ''}
          onClick={() => setMetric('visits')}
        >
          כניסות
        </button>
      </div>

      <nav className="stats-breadcrumb" aria-label="ניווט">
        <button
          type="button"
          className="stats-crumb"
          onClick={() => {
            setSelectedApp(null);
            setSelectedHospital(null);
          }}
          disabled={!selectedApp}
        >
          כל האפליקציות
        </button>
        {selectedApp && (
          <>
            <span className="stats-crumb-sep">›</span>
            <button
              type="button"
              className="stats-crumb"
              onClick={() => setSelectedHospital(null)}
              disabled={!selectedHospital}
            >
              {selectedApp.label}
            </button>
          </>
        )}
        {selectedHospital && (
          <>
            <span className="stats-crumb-sep">›</span>
            <span className="stats-crumb is-current">{selectedHospital.label}</span>
          </>
        )}
      </nav>

      {loading && <p className="stats-loading">טוען…</p>}

      {!loading && rows.length === 0 && (
        <p className="stats-empty">
          {unavailable ? 'אין נתונים להצגה כרגע.' : 'אין נתוני שימוש בטווח שנבחר.'}
        </p>
      )}

      {!loading && rows.length > 0 && (
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th scope="col">{scopeHeader}</th>
                {bucketColumns.map((bucketStart) => (
                  <th key={bucketStart} scope="col">
                    {formatBucketLabel(bucketStart, period.granularity)}
                  </th>
                ))}
                <th scope="col" className="stats-total-col">
                  סה״כ
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const appKey = row.appName || row.key;
                const app: Selection = { key: appKey, label: row.label };
                const expanded = isAppScope && expandedApps.includes(appKey);
                const breakdown = expanded ? breakdowns[breakdownKey(appKey)] : undefined;
                // Only the hospital view drills; app rows expand in place, and
                // department rows are the last level.
                const drill = scope === 'hospital' && selectedApp
                  ? () => drillIntoHospital(selectedApp, row)
                  : undefined;
                const activate = isAppScope ? () => toggleApp(appKey) : drill;

                return (
                  <React.Fragment key={row.key}>
                    <tr
                      className={[
                        activate ? 'is-drillable' : '',
                        expanded ? 'is-expanded' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-expanded={isAppScope ? expanded : undefined}
                      {...activationProps(activate)}
                    >
                      <th scope="row">
                        {isAppScope && (
                          <span className="stats-expander" aria-hidden="true">
                            {expanded ? '▾' : '▸'}
                          </span>
                        )}
                        {row.label}
                      </th>
                      {bucketColumns.map((bucketStart) => (
                        <td key={bucketStart}>{valueFor(row, bucketStart) || '—'}</td>
                      ))}
                      <td className="stats-total-col">
                        {metric === 'users' ? row.users : row.visits}
                      </td>
                    </tr>

                    {expanded && breakdown?.loading && (
                      <tr className="stats-subrow">
                        <td colSpan={fullWidth} className="stats-subrow-note">
                          טוען…
                        </td>
                      </tr>
                    )}

                    {expanded && breakdown && !breakdown.loading && breakdown.rows.length === 0 && (
                      <tr className="stats-subrow">
                        <td colSpan={fullWidth} className="stats-subrow-note">
                          {breakdown.unavailable
                            ? 'אין נתונים להצגה כרגע.'
                            : 'אין פילוח לפי בית חולים בטווח שנבחר.'}
                        </td>
                      </tr>
                    )}

                    {expanded &&
                      breakdown &&
                      !breakdown.loading &&
                      breakdown.rows.map((hospitalRow) => (
                        <tr
                          key={`${row.key}|${hospitalRow.key}`}
                          className="stats-subrow is-drillable"
                          {...activationProps(() => drillIntoHospital(app, hospitalRow))}
                        >
                          <th scope="row">{hospitalRow.label}</th>
                          {bucketColumns.map((bucketStart) => (
                            <td key={bucketStart}>{valueFor(hospitalRow, bucketStart) || '—'}</td>
                          ))}
                          <td className="stats-total-col">
                            {metric === 'users' ? hospitalRow.users : hospitalRow.visits}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="stats-note">
        {isAppScope
          ? 'לחיצה על שורת אפליקציה פותחת את הפילוח שלה לפי בית חולים; לחיצה על בית חולים מציגה את הפילוח לפי מחלקה. '
          : scope === 'hospital'
            ? 'לחיצה על בית חולים מציגה את הפילוח לפי מחלקה. '
            : ''}
        {metric === 'users'
          ? 'משתמש שנכנס במספר ימים נספר פעם אחת בסה״כ. משתמש המשויך לכמה מחלקות או לכמה בתי חולים נספר בכל אחד מהם, ולכן סכום השורות עשוי להיות גבוה מהמספר ברמת האפליקציה. ברמת האפליקציה כל משתמש נספר פעם אחת בלבד.'
          : 'כניסה נספרת בכל פעם שנכנסים לאפליקציה. כניסה של משתמש המשויך לכמה מחלקות או בתי חולים נספרת בכל אחד מהם, ולכן אין לסכום שורות בין רמות שונות.'}{' '}
        בית חולים ומחלקה נגזרים משיוך המשתמש.
      </p>
    </section>
  );
};

export default AppUsageSection;
